import { SignUpFormField } from "../constants/signup-form-field.enum";
import { GymSubtype, GymType } from "../../../constants/gymSubtypes";
import { getGymTypeFromSubtype } from "../../../utils/gymTypes";
import { getIso2CodeFromCountry } from "../../../utils/countries";
import { formatHubspotMultiSelectOptions, formatUrl } from "./index";
import Cookies from "js-cookie";
import type { FormData } from "../machines/signup.control.machine";

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  [key: string]: string | undefined;
}

export interface SignUpPayload {
  client: {
    company: string;
    averageMonthlyRevenue: string;
    gymType: GymType;
    gymSubtype: GymSubtype;
    url: string | null;
    currentSoftware: string;
    yearsOpen: string;
    address: {
      city: string;
      state: string;
      addressLine1: string;
      postalCode: string;
      country: string;
    };
    businessNeeds: { value: string }[];
  };
  user: {
    email: string;
    phone: string;
    firstName: string;
    lastName: string;
  };
  meta: {
    plan: { id: "pp-prod-core-free"; cancelAtPeriodEnd: 0 };
    utm: {
      source: string;
      medium: string;
      campaign: string;
      content: string;
      term: string;
    };
  };
}

/**
 * Build the payload for the sign up API from the machine context.
 * @param context - The XState machine context containing formData and utm
 * @returns SignUpPayload
 */
export function getSignUpPayload(context: {
  formData: FormData;
  utm?: UTMParams;
}): SignUpPayload {
  const { formData, utm } = context;
  const gymSubtype: GymSubtype = String(
    formData[SignUpFormField.Vertical] || "",
  ) as GymSubtype;
  const companyName = String(
    formData[SignUpFormField.Company]?.label?.split(",")[0] || "",
  ).trim();
  const firstName = String(formData[SignUpFormField.FirstName] || "").trim();
  const lastName = String(formData[SignUpFormField.LastName] || "").trim();
  const email = String(formData[SignUpFormField.Email] || "").trim();
  const phoneNumber =
    formData[SignUpFormField.Phone] || formData["phoneNumber"] || "";
  const websiteUrl = formatUrl(formData[SignUpFormField.Url] || "");
  const country = formData[SignUpFormField.Country];
  const gymType = getGymTypeFromSubtype(gymSubtype) as GymType;
  const yearsOpen = formData[SignUpFormField.YearsOpen];
  const monthlyRevenue = formData[SignUpFormField.Revenue];
  const businessNeeds = formatHubspotMultiSelectOptions(
    formData[SignUpFormField.BusinessNeeds] || [],
  );
  const currentSoftware = formData[SignUpFormField.CurrentSoftware] || "";

  const utmSource = utm?.utm_source ?? Cookies.get("utm_source") ?? "";
  const utmMedium = utm?.utm_medium ?? Cookies.get("utm_medium") ?? "";
  const utmCampaign = utm?.utm_campaign ?? Cookies.get("utm_campaign") ?? "";
  const utmContent = utm?.utm_content ?? Cookies.get("utm_content") ?? "";
  const utmTerm = utm?.utm_term ?? Cookies.get("utm_term") ?? "";

  return {
    client: {
      company: companyName,
      averageMonthlyRevenue: monthlyRevenue,
      gymType,
      gymSubtype,
      url: websiteUrl,
      currentSoftware,
      yearsOpen,
      address: {
        city: "",
        state: "",
        addressLine1: "",
        postalCode: "",
        country: getIso2CodeFromCountry(country),
      },
      businessNeeds: businessNeeds.split(";").map((need) => ({ value: need })),
    },
    user: { email, phone: String(phoneNumber), firstName, lastName },
    meta: {
      plan: { id: "pp-prod-core-free", cancelAtPeriodEnd: 0 },
      utm: {
        source: utmSource,
        medium: utmMedium,
        campaign: utmCampaign,
        content: utmContent,
        term: utmTerm,
      },
    },
  };
}
